import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Gift, PartyPopper, Sparkles, Sun } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { firstAvailable, img } from "@/lib/api";
import { AddToBagButton } from "@/components/Cta";
import { Availability, Price } from "@/components/ProductBits";

const OPTIONS = [
  { key: "everyday", label: "Everyday Elegance", Icon: Sun, line: "A small piece with a big presence, for work, temple visits and family time." },
  { key: "festive", label: "Festive Moments", Icon: Sparkles, line: "Made to catch the glow of diyas, poojas and festival lights." },
  { key: "celebrations", label: "Special Celebrations", Icon: PartyPopper, line: "For weddings, vratams and the photographs you'll keep forever." },
  { key: "gifting", label: "Thoughtful Gifting", Icon: Gift, line: "A gift that says you were paying attention." },
];

const pick = (products, key) =>
  products.filter((p) => p.occasions?.includes(key)).sort((a, b) => a.occasions.indexOf(key) - b.occasions.indexOf(key) || a.order - b.order)[0] || products[0];

export const Occasion = () => {
  const { site, products, addToBag } = useStore();
  const [sel, setSel] = useState("everyday");
  const opt = OPTIONS.find((o) => o.key === sel);
  const p = pick(products, sel);
  if (!p) return null;
  return (
    <section data-testid="occasion-section" className="bg-lilac py-20 lg:py-28">
      <div className="container-rh grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <p className="eyebrow text-berry mb-5">A little guidance</p>
          <h2 className="font-display font-medium text-[40px] sm:text-[52px] leading-[1]">{site.occasion.heading}</h2>
          <p className="mt-5 text-[17px] text-plum/75">{site.occasion.subheading}</p>
          <div className="mt-9 grid grid-cols-2 gap-3" role="radiogroup">
            {OPTIONS.map(({ key, label, Icon }) => {
              const on = key === sel;
              return (
                <button
                  key={key}
                  role="radio"
                  aria-checked={on}
                  onClick={() => setSel(key)}
                  data-testid={`occasion-option-${key}`}
                  className={`text-left rounded-[16px] p-4 sm:p-5 border transition-colors duration-300 ${on ? "bg-plum text-pearl border-plum" : "bg-white/70 border-plum/10 hover:border-plum/40"}`}
                >
                  <Icon className={`w-5 h-5 mb-4 ${on ? "text-champagne" : "text-berry"}`} strokeWidth={1.8} />
                  <span className="block font-bold text-[15px] sm:text-[16px] leading-snug">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            <motion.div
              key={sel}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
              data-testid="occasion-result"
              className="grid sm:grid-cols-2 gap-3 items-center rounded-[22px] bg-ivory p-3 shadow-[0_24px_60px_-30px_rgba(48,32,45,.35)]"
            >
              <Link to={`/product/${p.slug}`} className="block rounded-[18px] overflow-hidden">
                <img src={img(p.images[0])} alt={p.name} loading="lazy" className="block w-full h-auto hover:scale-105 transition-transform duration-700" />
              </Link>
              <div className="p-4 sm:p-6 flex flex-col justify-center gap-4">
                <p className="eyebrow text-berry">Your match · {opt.label}</p>
                <h3 className="font-display text-[32px] sm:text-[36px] leading-[1.02] font-medium" data-testid="occasion-result-name">{p.name}</h3>
                <p className="text-[16px] text-plum/75 leading-relaxed">{opt.line}</p>
                <Price p={p} />
                <Availability p={p} />
                <div className="flex flex-wrap items-center gap-5 pt-1">
                  <AddToBagButton testId="occasion-add-to-bag" size="sm" disabled={p.available <= 0} onAdd={() => addToBag(p, firstAvailable(p), 1)} />
                  <Link to={`/product/${p.slug}`} className="rh-link" data-testid="occasion-view-details">View</Link>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
